<!--
/*
 * Компонент AuthPage — форма входа и регистрации по email через magic link
 */
-->
<script setup lang="ts">
import AppFooter from '@/components/AppFooter.vue'
import { ref, computed, onMounted, watch } from 'vue'
import { useIntervalFn } from '@vueuse/core'

function useCooldown(seconds = 60) {
  const remaining = ref(0)
  const { pause, resume } = useIntervalFn(
    () => {
      remaining.value--
      if (remaining.value <= 0) pause()
    },
    1000,
    { immediate: false },
  )
  const start = () => {
    remaining.value = seconds
    resume()
  }
  return { remaining, start }
}

import { useAuthStore } from '@/modules/auth/stores/authStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { usePayStore } from '@/modules/pay/store/payStore'
import { useApiStore } from '@/modules/api/store/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { EventLogger } from '@/utils/EventLogger'
import { validateEmail } from '@/utils/validateEmail'
import { useSettingsStore } from '@/modules/settings/store/settingsStore'
import SupportButton from '@/components/support-button/SupportButton.vue'
import OtpCodeInput from '@/modules/auth/components/OtpCodeInput.vue'
import { useRoute, useRouter } from 'vue-router'
import { ERouteNames } from '@/router/ERouteNames'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'

const { mobile } = useDisplay()
const { t } = useI18n()

const authStore = useAuthStore()
const userStore = useUserStore()
const settingsStore = useSettingsStore()
const payStore = usePayStore()
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()
const route = useRoute()
const router = useRouter()

const mode = ref<'login' | 'register'>('login')
const email = ref('')
const firstName = ref('')
const error = ref('')
const info = ref('')
const isLoading = ref(false)
const showOtp = ref(false)
const otpError = ref('')
const referralCode = ref('')

const switchMode = (newMode: 'login' | 'register') => {
  mode.value = newMode
  error.value = ''
  info.value = ''
  showOtp.value = false
}

const POLICY_DISCLOSURE_KEY = 'vpn_disclosure_accepted_v1'

const isPolicyAccepted = ref(
  localStorage.getItem(POLICY_DISCLOSURE_KEY) !== 'false',
)

const DEFAULT_PRIVACY_POLICY_URL = 'https://docs.google.com/document/d/1-JcPn5i-qEtzIBa_qlIYsIm2jJilVwoynC-lckVN0B8/edit?usp=sharing'
const DEFAULT_PERSONAL_DATA_CONSENT_URL = 'https://docs.google.com/document/d/170TZHIhXxvylcq4IJaqN-XyBDo498jTGmPip2XKH0oY/edit?usp=sharing'

const privacyPolicyUrl = (import.meta.env.VITE_APP_PRIVACY_POLICY_URL as string) || DEFAULT_PRIVACY_POLICY_URL
const personalDataConsentUrl = (import.meta.env.VITE_APP_PERSONAL_DATA_CONSENT_URL as string) || DEFAULT_PERSONAL_DATA_CONSENT_URL

function openPrivacyPolicy(): void {
  window.open(privacyPolicyUrl, '_blank')
}

function openTerms(): void {
  window.open(personalDataConsentUrl, '_blank')
}

watch(
  isPolicyAccepted,
  (accepted) => {
    if (accepted) {
      localStorage.setItem(POLICY_DISCLOSURE_KEY, 'true')
    } else {
      localStorage.setItem(POLICY_DISCLOSURE_KEY, 'false')
    }
  },
  { immediate: true },
)

const { remaining: loginCooldown, start: startLoginCooldown } = useCooldown(60)
const { remaining: registerCooldown, start: startRegisterCooldown } = useCooldown(60)

const cooldown = computed(() =>
  mode.value === 'login' ? loginCooldown.value : registerCooldown.value,
)

const handleTelegramOidcLogin = async () => {
  const botId = settingsStore.telegramBotId
  if (!botId) {
    error.value = t('errors.telegramBotIdMissing')
    return
  }

  isLoading.value = true
  error.value = ''

  try {
    const loginResult = await new Promise<ITelegramLoginResult>((resolve, reject) => {
      const TelegramLogin = window.Telegram?.Login
      if (!TelegramLogin) {
        reject(new Error(t('errors.telegramLoginUnavailable')))
        return
      }

      TelegramLogin.auth(
        { client_id: String(botId), request_access: 'write' },
        (data) => {
          if (!data) {
            reject(new Error(t('errors.telegramAuthCancelled')))
            return
          }
          if (data.error) {
            reject(new Error(data.error))
            return
          }
          resolve(data)
        }
      )
    })

    const isLinkAction = localStorage.getItem('app_action') === 'link'
    let success = false

    if (isLinkAction) {
      const linkResult = await authStore.linkTelegramOIDC({ idToken: loginResult.id_token })
      success = linkResult.ok
      if (!success) {
        throw new Error(linkResult.error || t('errors.telegramLinkFailed'))
      }
    } else {
      const oidcResult = await authStore.telegramOIDCLogin({ idToken: loginResult.id_token })
      success = oidcResult.ok
      if (!success) {
        throw new Error(oidcResult.error || t('errors.telegramLoginFailed'))
      }
    }

    if (success) {
      const fromApp = localStorage.getItem('app_from') === 'app'
      const redirectUrl = localStorage.getItem('app_redirect_url')

      if (fromApp && redirectUrl) {
        localStorage.removeItem('app_from')
        localStorage.removeItem('app_redirect_url')
        localStorage.removeItem('app_action')
        
        const { accessToken, refreshToken } = authStore
        const actionParam = isLinkAction ? '&action=link' : ''
        window.location.href = `${redirectUrl}auth-callback?accessToken=${accessToken}&refreshToken=${refreshToken}${actionParam}`
        return
      }

      await Promise.all([
        userStore.getUserData(),
        settingsStore.getSettings(),
        apiStore.getUserToServers(),
      ])
      if (isLinkAction) {
        await router.replace({ name: ERouteNames.PROFILE })
      } else {
        const paymentStarted = await payStore.tryStartPendingPayment()
        if (router.currentRoute.value.name !== ERouteNames.PROFILE) {
          await router.replace({ name: paymentStarted ? ERouteNames.PAYMENT : ERouteNames.HOME })
        }
      }
    }
  } catch (err) {
    EventLogger.error('AuthPage.handleTelegramOidcLogin', [`Ошибка: ${(err as Error).message}`])
    error.value = (err as Error).message || t('errors.telegramAuthError')
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  if (!settingsStore.isLoaded) {
    await settingsStore.getSettings()
  }

  const from = route.query.from as string
  const redirectUrl = route.query.redirect_url as string
  const action = route.query.action as string
  const urlAccessToken = route.query.accessToken as string
  const urlRefreshToken = route.query.refreshToken as string

  if (from === 'app' && redirectUrl) {
    localStorage.setItem('app_from', from)
    localStorage.setItem('app_redirect_url', redirectUrl)
    
    if (action === 'link' && urlAccessToken && urlRefreshToken) {
      localStorage.setItem('app_action', 'link')
      localStorage.setItem('vpn_access_token', urlAccessToken)
      localStorage.setItem('vpn_refresh_token', urlRefreshToken)
      authStore.accessToken = urlAccessToken
      authStore.refreshToken = urlRefreshToken
      authStore.isAuth = true
    }
  }

  if (from === 'app' && redirectUrl) {
    if (authStore.isTelegramLoginAvailable) {
      handleTelegramOidcLogin()
    } else {
      const unwatch = watch(
        () => authStore.isTelegramLoginAvailable,
        (available) => {
          if (available) {
            handleTelegramOidcLogin()
            unwatch()
          }
        },
      )
    }
  }

  const urlReferral = route.query.referral_code as string
  if (urlReferral) {
    localStorage.setItem('referral_code', urlReferral)
    referralCode.value = urlReferral
  } else {
    referralCode.value = localStorage.getItem('referral_code') || ''
  }

  if (referralCode.value) {
    mode.value = 'register'
  }
})

const handleSignIn = async () => {
  error.value = ''
  info.value = ''

  if (!isPolicyAccepted.value) {
    error.value = t('errors.policyRequired')
    return
  }

  if (!validateEmail(email.value)) {
    error.value = t('errors.invalidEmail')
    return
  }

  isLoading.value = true
  feedbackStore.isGlobalLoading = true
  try {
    const res = await authStore.goSignIn({ email: email.value })

    if (res.ok) {
      info.value = t('auth.codeSentTo')
      showOtp.value = true
      otpError.value = ''
      startLoginCooldown()
    } else {
      const message = res.error || t('errors.accountNotFound')
      const isNotFound =
        message === t('errors.accountNotFound') ||
        /не найден/i.test(message) ||
        /not found/i.test(message)

      // «Пользователь не найден» — переключаем на регистрацию (info).
      // Сеть / 504 / прочий сбой — остаёмся на входе с error-плашкой.
      if (isNotFound) {
        mode.value = 'register'
        info.value = message
      } else {
        error.value = message
      }
    }
  } catch (err: unknown) {
    EventLogger.error('AuthPage.handleSignIn', [`Ошибка: ${(err as Error).message}`])
    error.value = t('errors.tryLater')
  } finally {
    isLoading.value = false
    feedbackStore.isGlobalLoading = false
  }
}

const handleSignUp = async () => {
  error.value = ''
  info.value = ''

  if (!isPolicyAccepted.value) {
    error.value = t('errors.policyRequired')
    return
  }

  if (!validateEmail(email.value)) {
    error.value = t('errors.invalidEmail')
    return
  }

  isLoading.value = true
  feedbackStore.isGlobalLoading = true
  try {
    const res = await authStore.goSignUp({
      email: email.value,
      ...(firstName.value ? { firstName: firstName.value } : {}),
      ...(referralCode.value ? { referrerCode: referralCode.value } : {}),
    })

    if (res.ok) {
      info.value = t('auth.codeSentTo')
      showOtp.value = true
      otpError.value = ''
      startRegisterCooldown()
      localStorage.removeItem('referral_code')
    } else {
      EventLogger.error('AuthPage.handleSignUp', [`Ошибка регистрации: ${res.error}`])
      error.value = res.error || t('errors.signUpFailed')
    }
  } catch (err: unknown) {
    EventLogger.error('AuthPage.handleSignUp', [`Ошибка: ${(err as Error).message}`])
    error.value = t('errors.tryLater')
  } finally {
    isLoading.value = false
    feedbackStore.isGlobalLoading = false
  }
}

const handleSubmit = async () => {
  if (mode.value === 'login') {
    await handleSignIn()
  } else {
    await handleSignUp()
  }
}

const handleOtpComplete = async (code: string) => {
  otpError.value = ''
  isLoading.value = true
  feedbackStore.isGlobalLoading = true
  try {
    const result = await authStore.validateMagicLink({ email: email.value, token: code })

    if (result.ok) {
      await Promise.all([
        userStore.getUserData(),
        settingsStore.getSettings(),
        apiStore.getUserToServers(),
      ])
      const paymentStarted = await payStore.tryStartPendingPayment()
      if (router.currentRoute.value.name !== ERouteNames.PROFILE) {
        await router.replace({ name: paymentStarted ? ERouteNames.PAYMENT : ERouteNames.HOME })
      }
    } else {
      otpError.value = result.error || t('errors.invalidCode')
    }
  } catch (err: unknown) {
    EventLogger.error('AuthPage.handleOtpComplete', [`Ошибка: ${(err as Error).message}`])
    otpError.value = t('errors.tryLater')
  } finally {
    isLoading.value = false
    feedbackStore.isGlobalLoading = false
  }
}

const handleOtpResend = async () => {
  if (mode.value === 'login') {
    await handleSignIn()
  } else {
    await handleSignUp()
  }
}
</script>

<template>
  <div class="auth-page-wrapper d-flex flex-column align-center justify-space-between flex-grow-1 w-100 px-4 py-2">
    <div class="d-flex flex-column align-center justify-center flex-grow-1 w-100 auth-inner-container">
      
      <div class="container" :class="{ 'active': mode === 'register', 'is-mobile': mobile }">
        
        <div v-if="mobile" class="mobile-tabs-header">
          <button
            type="button"
            class="tab-btn"
            :class="{ active: mode === 'login' }"
            @click="switchMode('login')"
          >
            {{ t('auth.login') }}
          </button>
          <button
            type="button"
            class="tab-btn"
            :class="{ active: mode === 'register' }"
            @click="switchMode('register')"
          >
            {{ t('auth.register') }}
          </button>
        </div>

        <div class="form-container sign-up">
          <v-form @submit.prevent="handleSubmit" class="form-content pa-6">
            <div class="form-top-bar w-100 d-flex align-center justify-end mb-2">
              <v-btn
                variant="text"
                size="small"
                color="medium-emphasis"
                prepend-icon="ri-arrow-left-line"
                class="back-btn text-none pa-0"
                @click="router.push({ name: ERouteNames.HOME })"
              >
                {{ t('auth.toTariffs') }}
              </v-btn>
            </div>

            <h1 class="form-title">{{ t('auth.register') }}</h1>
            <p class="form-subtitle">{{ t('auth.fillRegDetails') }}</p>

            <v-chip v-if="referralCode" color="success" variant="tonal" size="small" class="mb-2">
              {{ t('auth.referralChip') }}
            </v-chip>

            <v-text-field
              v-model="firstName"
              :placeholder="t('auth.yourName')"
              variant="outlined"
              density="compact"
              hide-details="auto"
              rounded="lg"
              bg-color="#151822"
              append-inner-icon="ri-user-3-line"
              class="w-100 mb-2"
              :disabled="isLoading || !isPolicyAccepted"
            />

            <v-text-field
              v-model="email"
              type="email"
              placeholder="Email"
              autocomplete="email"
              variant="outlined"
              density="compact"
              hide-details="auto"
              rounded="lg"
              bg-color="#151822"
              append-inner-icon="ri-mail-line"
              class="w-100 mb-2"
              :disabled="isLoading || !isPolicyAccepted"
            />

            <label v-if="!showOtp && mode === 'register'" class="custom-checkbox-label w-100 my-1">
              <input
                type="checkbox"
                v-model="isPolicyAccepted"
                class="custom-checkbox-input"
              />
              <span class="custom-checkbox-box">
                <i class="ri-check-line check-icon"></i>
              </span>
              <span class="custom-checkbox-text">
                {{ t('auth.agreeToPolicy') }}
                <a href="#" class="policy-link" @click.stop.prevent="openPrivacyPolicy">{{ t('auth.privacyPolicy') }}</a>
                {{ t('auth.and') }}
                <a href="#" class="policy-link" @click.stop.prevent="openTerms">{{ t('auth.termsOfUse') }}</a>
              </span>
            </label>

            <div v-if="info && mode === 'register'" class="custom-alert info-alert">
              <i class="ri-information-line alert-icon"></i>
              <span>{{ info }}</span>
            </div>

            <div v-if="error && mode === 'register'" class="custom-alert error-alert">
              <i class="ri-error-warning-line alert-icon"></i>
              <span>{{ error }}</span>
            </div>

            <otp-code-input
              v-if="showOtp && mode === 'register'"
              :loading="isLoading"
              :error="otpError"
              @complete="handleOtpComplete"
              @resend="handleOtpResend"
            />

            <v-btn
              v-if="!showOtp"
              type="submit"
              block
              color="primary"
              variant="flat"
              density="comfortable"
              rounded="lg"
              class="mt-2 text-none font-weight-bold"
              :loading="isLoading"
              :disabled="isLoading || cooldown > 0 || !isPolicyAccepted"
            >
              <template v-if="cooldown > 0">
                {{ t('auth.resendCodeCount', { seconds: cooldown }) }}
              </template>
              <template v-else>
                {{ t('auth.registerBtn') }}
              </template>
            </v-btn>

            <div v-if="!showOtp && authStore.isTelegramLoginAvailable && settingsStore.telegramBotId" class="w-100 d-flex flex-column ga-2 mt-2">
              <div class="divider-line"><span>{{ t('auth.or') }}</span></div>
              <v-btn
                block
                color="#24A1DE"
                variant="flat"
                density="comfortable"
                rounded="lg"
                prepend-icon="ri-telegram-2-line"
                class="text-none font-weight-bold text-white"
                :disabled="isLoading || !isPolicyAccepted"
                @click="handleTelegramOidcLogin"
              >
                {{ t('auth.telegramLogin') }}
              </v-btn>
            </div>
          </v-form>
        </div>

        <div class="form-container sign-in">
          <v-form @submit.prevent="handleSubmit" class="form-content pa-6">
            <div class="form-top-bar w-100 d-flex align-center justify-start mb-2">
              <v-btn
                variant="text"
                size="small"
                color="medium-emphasis"
                prepend-icon="ri-arrow-left-line"
                class="back-btn text-none pa-0"
                style="margin-left: 18px;"
                @click="router.push({ name: ERouteNames.HOME })"
              >
                {{ t('auth.toTariffs') }}
              </v-btn>
            </div>

            <h1 class="form-title">{{ t('auth.login') }}</h1>
            <p class="form-subtitle">{{ t('auth.enterEmailSubtitle') }}</p>

            <v-text-field
              v-model="email"
              type="email"
              placeholder="Email"
              autocomplete="email"
              variant="outlined"
              density="compact"
              hide-details="auto"
              rounded="lg"
              bg-color="#151822"
              append-inner-icon="ri-mail-line"
              class="w-100 mb-2"
              :disabled="isLoading || !isPolicyAccepted"
            />

            <label v-if="!showOtp && mode === 'login'" class="custom-checkbox-label w-100 my-1">
              <input
                type="checkbox"
                v-model="isPolicyAccepted"
                class="custom-checkbox-input"
              />
              <span class="custom-checkbox-box">
                <i class="ri-check-line check-icon"></i>
              </span>
              <span class="custom-checkbox-text">
                {{ t('auth.agreeToPolicy') }}
                <a href="#" class="policy-link" @click.stop.prevent="openPrivacyPolicy">{{ t('auth.privacyPolicy') }}</a>
                {{ t('auth.and') }}
                <a href="#" class="policy-link" @click.stop.prevent="openTerms">{{ t('auth.termsOfUse') }}</a>
              </span>
            </label>

            <div v-if="info && mode === 'login'" class="custom-alert info-alert">
              <i class="ri-information-line alert-icon"></i>
              <span>{{ info }}</span>
            </div>

            <div v-if="error && mode === 'login'" class="custom-alert error-alert">
              <i class="ri-error-warning-line alert-icon"></i>
              <span>{{ error }}</span>
            </div>

            <otp-code-input
              v-if="showOtp && mode === 'login'"
              :loading="isLoading"
              :error="otpError"
              @complete="handleOtpComplete"
              @resend="handleOtpResend"
            />

            <v-btn
              v-if="!showOtp"
              type="submit"
              block
              color="primary"
              variant="flat"
              density="comfortable"
              rounded="lg"
              class="mt-2 text-none font-weight-bold"
              :loading="isLoading"
              :disabled="isLoading || cooldown > 0 || !isPolicyAccepted"
            >
              <template v-if="cooldown > 0">
                {{ t('auth.resendCodeCount', { seconds: cooldown }) }}
              </template>
              <template v-else>
                {{ t('auth.loginByEmail') }}
              </template>
            </v-btn>

            <div v-if="!showOtp && authStore.isTelegramLoginAvailable && settingsStore.telegramBotId" class="w-100 d-flex flex-column ga-2 mt-2">
              <div class="divider-line"><span>{{ t('auth.or') }}</span></div>
              <v-btn
                block
                color="#24A1DE"
                variant="flat"
                density="comfortable"
                rounded="lg"
                prepend-icon="ri-telegram-2-line"
                class="text-none font-weight-bold text-white"
                :disabled="isLoading || !isPolicyAccepted"
                @click="handleTelegramOidcLogin"
              >
                {{ t('auth.telegramLogin') }}
              </v-btn>
            </div>
          </v-form>
        </div>

        <div v-if="!mobile" class="toggle-container">
          <div class="toggle">
            <div class="toggle-panel toggle-left">
              <h1>{{ t('auth.welcomeBackTitle') }}</h1>
              <p>{{ t('auth.welcomeBackDesc') }}</p>
              <button type="button" class="toggle-btn" @click="switchMode('login')">{{ t('auth.welcomeBackBtn') }}</button>
            </div>

            <div class="toggle-panel toggle-right">
              <h1>{{ t('auth.joinTitle') }}</h1>
              <p>{{ t('auth.joinDesc') }}</p>
              <button type="button" class="toggle-btn" @click="switchMode('register')">{{ t('auth.joinBtn') }}</button>
            </div>
          </div>
        </div>

      </div>

      <div class="w-100 mt-3" style="max-width: 860px;">
        <support-button />
      </div>
    </div>

    <app-footer class="w-100 mt-auto" />
  </div>
</template>

<style scoped lang="scss">
$color-primary: #5237EA;
$color-primary-dark: #2E3999;
$color-surface: #1E2532;
$color-background: #151822;
$color-border: #2D3749;
$color-text-primary: #F7F7F7;
$color-text-muted: #8592AD;
$color-telegram: #24A1DE;
$color-error: #FF3737;

$color-info-bg: rgba(82, 55, 234, 0.15);
$color-info-border: rgba(116, 132, 237, 0.35);
$color-info-icon: #7484ED;

$color-error-bg: rgba(255, 55, 55, 0.15);
$color-error-border: rgba(255, 55, 55, 0.35);

$radius-card: 20px;
$radius-control: 10px;
$height-control: 42px;
$height-input: 40px;
$card-max-width: 840px;
$card-min-height: 540px;

.auth-page-wrapper {
  min-height: calc(100vh - 70px);
  box-sizing: border-box;
}

.auth-inner-container {
  width: 100%;
  max-width: $card-max-width;
}

.container {
  background-color: $color-surface;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: $radius-card;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.45), 0 0 30px rgba(82, 55, 234, 0.15);
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: $card-max-width;
  min-height: $card-min-height;
  height: auto;
  color: $color-text-primary;
  transition: all 0.5s ease;
}

.form-top-bar {
  width: 100%;
  margin-bottom: 12px;
}

.sign-in .form-top-bar {
  padding-left: 18px;
}

.sign-up .form-top-bar {
  padding-right: 24px !important;
}

.back-btn {
  background: transparent;
  border: none;
  color: $color-text-muted;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 2px 0;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    color: $color-text-primary;
  }
}

.form-container {
  position: absolute;
  top: 0;
  min-height: 100%;
  transition: all 0.6s ease-in-out;
}

.form-content {
  background-color: $color-surface;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  min-height: 100%;
  text-align: center;
}

.form-title {
  font-size: 22px;
  font-weight: 800;
  margin-bottom: 2px;
  color: $color-text-primary;
  letter-spacing: -0.3px;
}

.form-subtitle {
  font-size: 13px;
  color: $color-text-muted;
  margin-bottom: 12px;
}

.sign-in {
  left: 0;
  width: 50%;
  z-index: 2;
}

.container.active .sign-in {
  transform: translateX(100%);
}

.sign-up {
  left: 0;
  width: 50%;
  opacity: 0;
  z-index: 1;
}

.container.active .sign-up {
  transform: translateX(100%);
  opacity: 1;
  z-index: 5;
  animation: move 0.6s;
}

:deep(.form-content .v-text-field) {
  width: 100%;
  flex: 0 0 auto !important;

  .v-input__control {
    min-height: $height-input !important;
    height: $height-input !important;
  }

  .v-field {
    border-radius: $radius-control !important;
    background-color: $color-background !important;
    min-height: $height-input !important;
    max-height: $height-input !important;
    height: $height-input !important;
    box-shadow: none !important;
  }

  .v-field__field {
    height: $height-input !important;
    display: flex !important;
    align-items: center !important;
  }

  .v-field__input {
    min-height: $height-input !important;
    max-height: $height-input !important;
    height: $height-input !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: 14px !important;
    color: $color-text-primary !important;
  }

  .v-field__append-inner {
    padding-top: 0 !important;
    height: $height-input !important;
    align-items: center !important;
    color: $color-text-muted !important;
  }

  .v-field__outline {
    --v-field-border-opacity: 0.5 !important;
    color: $color-border !important;
  }
}

:deep(.form-content .v-btn:not(.back-btn)) {
  height: $height-control !important;
  min-height: $height-control !important;
  max-height: $height-control !important;
  flex: 0 0 $height-control !important;
  border-radius: $radius-control !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;

  &.v-btn--disabled {
    background-color: rgb(var(--v-theme-disabled)) !important;
    color: rgb(var(--v-theme-text-disabled)) !important;
    opacity: 0.9 !important;

    .v-btn__content {
      color: rgb(var(--v-theme-text-disabled)) !important;
    }
  }
}

:deep(.v-btn.back-btn) {
  height: 26px !important;
  min-height: 26px !important;
  max-height: 26px !important;
  flex: 0 0 26px !important;
  color: $color-text-muted !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  margin-left: 6px !important;

  .v-btn__prepend {
    margin-inline-end: 4px !important;
    margin-inline-start: 0 !important;
  }

  .v-btn__overlay,
  .v-btn__underlay {
    display: none !important;
    opacity: 0 !important;
  }

  &:hover {
    color: $color-text-primary !important;
    background: transparent !important;

    .v-btn__content,
    .v-icon {
      color: $color-text-primary !important;
    }
  }
}

.custom-alert {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: $radius-control;
  font-size: 13px;
  line-height: 1.35;
  text-align: left;
  margin: 6px 0;

  span {
    flex: 1;
  }
}

.custom-checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  text-align: left;
  margin: 6px 0;
  width: 100%;
}

.custom-checkbox-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + .custom-checkbox-box {
    background-color: $color-primary;
    border-color: $color-primary;
    box-shadow: 0 2px 8px rgba(82, 55, 234, 0.4);

    .check-icon {
      opacity: 1;
      transform: scale(1);
    }
  }
}

.custom-checkbox-box {
  width: 18px;
  height: 18px;
  min-width: 18px;
  border-radius: 5px;
  border: 1px solid $color-border;
  background-color: $color-background;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0;
  transition: all 0.2s ease;

  .check-icon {
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    opacity: 0;
    transform: scale(0.6);
    transition: all 0.2s ease;
  }
}

.custom-checkbox-text {
  font-size: 12px;
  line-height: 1.4;
  color: $color-text-muted;

  .policy-link {
    color: $color-info-icon;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      text-decoration: underline;
      color: $color-text-primary;
    }
  }
}

.info-alert {
  background: $color-info-bg;
  border: 1px solid $color-info-border;
  color: $color-text-primary;

  .alert-icon {
    color: $color-info-icon;
    font-size: 18px;
    flex-shrink: 0;
  }
}

.error-alert {
  background: $color-error-bg;
  border: 1px solid $color-error-border;
  color: $color-text-primary;

  .alert-icon {
    color: $color-error;
    font-size: 18px;
    flex-shrink: 0;
  }
}

.divider-line {
  display: flex;
  align-items: center;
  text-align: center;
  color: $color-text-muted;
  font-size: 12px;
  opacity: 0;
  z-index: 1;
}

@keyframes move {
  0%, 49.99% {
    opacity: 0;
    z-index: 1;
  }
  50%, 100% {
    opacity: 1;
    z-index: 5;
  }
}

.toggle-container {
  position: absolute;
  top: 0;
  left: 50%;
  width: 50%;
  height: 100%;
  overflow: hidden;
  transition: all 0.6s ease-in-out;
  border-radius: 120px 0 0 80px;
  z-index: 1000;
}

.container.active .toggle-container {
  transform: translateX(-100%);
  border-radius: 0 120px 80px 0;
}

.toggle {
  background: linear-gradient(135deg, #5237EA 0%, #2E3999 100%);
  height: 100%;
  color: #ffffff;
  position: relative;
  left: -100%;
  width: 200%;
  transform: translateX(0);
  transition: all 0.6s ease-in-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 60%);
    pointer-events: none;
  }
}

.container.active .toggle {
  transform: translateX(50%);
}

.toggle-panel {
  position: absolute;
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0 24px;
  text-align: center;
  top: 0;
  transform: translateX(0);
  transition: all 0.6s ease-in-out;

  h1 {
    font-size: 20px;
    font-weight: 800;
    margin-bottom: 10px;
    letter-spacing: 0.5px;
    color: #ffffff;
    white-space: nowrap;
  }

  p {
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 0.2px;
    margin-bottom: 20px;
    color: rgba(255, 255, 255, 0.85);
  }
}

.toggle-left {
  transform: translateX(-20%);
}

.container.active .toggle-left {
  transform: translateX(0);
}

.toggle-right {
  right: 0;
  transform: translateX(0);
}

.container.active .toggle-right {
  transform: translateX(20%);
}

.toggle-btn {
  background-color: transparent;
  border: 2px solid #ffffff;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 30px;
  border-radius: 8px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #ffffff;
    color: #2E3999;
  }
}

.switch-mobile-link {
  font-size: 13px;
  color: #8592AD;

  a {
    color: #7484ED;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.container.is-mobile {
  width: 100%;
  min-height: auto;
  border-radius: 20px;

  .form-container {
    position: relative;
    width: 100%;
    height: auto;
    opacity: 1;
    transform: none !important;
    display: none;
  }

  &.active .sign-up {
    display: block;
  }

  &:not(.active) .sign-in {
    display: block;
  }

  .mobile-tabs-header {
    display: flex;
    width: 100%;
    background: #151822;
    padding: 6px;
    gap: 6px;
    border-bottom: 1px solid #2D3749;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;

    .tab-btn {
      flex: 1 1 50%;
      height: 40px;
      padding: 0 16px;
      border: none;
      background: transparent;
      border-radius: 10px;
      font-weight: 600;
      font-size: 14px;
      color: #8592AD;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;

      &.active {
        background: #5237EA;
        color: #F7F7F7;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba(82, 55, 234, 0.3);
      }
    }
  }
}
</style>
